/**
 * Endpoints de **Mantenimiento → Activos** (`/api/mantenimiento/activos`).
 *
 * El listado acepta `page`, `per_page`, `search`, `categoria_id` y `estado`; la
 * pantalla pide una página amplia y afina en el cliente (buscador, categoría y
 * estado), porque cada estación tiene pocos activos.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  ActivoDetalle,
  ActivoEstadoRespuesta,
  ActivoGuardado,
  ActivoPayload,
  ActivosCatalogos,
  ActivosFiltros,
  ActivosRespuesta,
} from './activos.types';

export const activosKeys = {
  todas: ['mantenimiento', 'activos'] as const,
  lista: (filtros: ActivosFiltros) => ['mantenimiento', 'activos', filtros] as const,
  catalogos: ['mantenimiento', 'activos', 'catalogos'] as const,
  detalle: (id: number) => ['mantenimiento', 'activos', 'detalle', id] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: ActivosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.categoriaId) parametros.set('categoria_id', String(filtros.categoriaId));
  if (filtros.estado) parametros.set('estado', filtros.estado);

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const activosApi = {
  listar: (filtros: ActivosFiltros = {}, signal?: AbortSignal): Promise<ActivosRespuesta> =>
    apiClient.get<ActivosRespuesta>(`/api/mantenimiento/activos${cadenaDe(filtros)}`, { signal }),
  catalogos: (signal?: AbortSignal): Promise<ActivosCatalogos> =>
    apiClient.get<ActivosCatalogos>('/api/mantenimiento/activos/catalogos', { signal }),
  detalle: (id: number): Promise<ActivoDetalle> =>
    apiClient.get<ActivoDetalle>(`/api/mantenimiento/activos/${id}`),
  crear: (payload: ActivoPayload): Promise<ActivoGuardado> =>
    apiClient.post<ActivoGuardado>('/api/mantenimiento/activos', { body: payload }),
  actualizar: (id: number, payload: ActivoPayload): Promise<ActivoGuardado> =>
    apiClient.put<ActivoGuardado>(`/api/mantenimiento/activos/${id}`, { body: payload }),
  cambiarEstado: (id: number, activo: boolean): Promise<ActivoEstadoRespuesta> =>
    apiClient.patch<ActivoEstadoRespuesta>(`/api/mantenimiento/activos/${id}/estado`, {
      body: { activo },
    }),
};

/** Listado de activos. */
export function useActivos(filtros: ActivosFiltros = {}) {
  return useQuery({
    queryKey: activosKeys.lista(filtros),
    queryFn: ({ signal }) => activosApi.listar(filtros, signal),
  });
}

/** Categorías y estados para los filtros y el formulario. */
export function useActivosCatalogos() {
  return useQuery({
    queryKey: activosKeys.catalogos,
    queryFn: ({ signal }) => activosApi.catalogos(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/** Detalle del activo con sus sub-activos. */
export function useActivoDetalle(id: number | null) {
  return useQuery({
    queryKey: activosKeys.detalle(id ?? 0),
    queryFn: () => activosApi.detalle(id ?? 0),
    enabled: id !== null,
  });
}

/** Alta de un activo. */
export function useCrearActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: activosApi.crear,
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: activosKeys.todas });
    },
  });
}

/** Edición de un activo. */
export function useActualizarActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ActivoPayload }) =>
      activosApi.actualizar(id, payload),
    onSuccess: () => cliente.invalidateQueries({ queryKey: activosKeys.todas }),
  });
}

/** Marcar un activo como activo o en desuso. */
export function useCambiarEstadoActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      activosApi.cambiarEstado(id, activo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: activosKeys.todas }),
  });
}
